import type { SpaceCredits } from '@/shared/types/Space';

import { TEAMSPACE_QUERY_KEYS } from '@/entities/teamspace/lib/teamspaceQueryKeys';

import { API } from '@/shared/api';
import { queryClient } from '@/shared/config';
import { USER_DATA_QUERY_KEY } from '@/shared/constants/queryKeys';

import { ChunkUploadQueue } from '../lib/chunkUploadQueue';
import { RecordingAnalytics } from '../lib/recorderAnalytics';
import { createEmptyRecordingMedia, RecordingMedia, RecordingRecorders } from '../lib/recorderMedia';
import { chargeSpaceRecordingMinutes, getCaptureEvents, getUploadedTracks } from '../lib/recordingFinish';

/** What one recording holds on to, from the start until it is saved or thrown away. */
export type RecordingSession = {
  /** Set when the recording starts and taken by whatever ends it, so the end is reported once. */
  analytics: null | RecordingAnalytics;
  media: RecordingMedia;
  recorders: RecordingRecorders;
  recordingId: null | string;
  stopped: null | StoppedRecording;
  upload: ChunkUploadQueue | null;
};

export type StoppedRecording = {
  hasCamera: boolean;
  isUploadCompleted: boolean;
  recordingDurationS: number;
};

export const createIdleSession = (): RecordingSession => ({
  analytics: null,
  media: createEmptyRecordingMedia(),
  recorders: { camera: null, main: null },
  recordingId: null,
  stopped: null,
  upload: null,
});

export const takeAnalytics = (session: RecordingSession) => {
  const { analytics } = session;
  session.analytics = null;
  return analytics;
};

export const abortServerRecordingQuietly = async (recordingId: string, spaceId: string) => {
  try {
    await API.abortRecordingUpload(recordingId, spaceId);
  } catch {}
};

type SaveRecordingParams = {
  isRecordingUnlimited?: boolean;
  recordingId: string;
  spaceId: string;
  stopped: StoppedRecording;
};

export const saveRecording = async ({ isRecordingUnlimited, recordingId, spaceId, stopped }: SaveRecordingParams) => {
  const { hasCamera, recordingDurationS } = stopped;
  if (!stopped.isUploadCompleted) {
    await API.completeRecordingUpload(recordingId, getUploadedTracks(hasCamera), spaceId);
    stopped.isUploadCompleted = true;
  }
  await API.finishRecordingUpload({ events: getCaptureEvents(hasCamera), recordingDurationS, recordingId });
  void queryClient.invalidateQueries({ queryKey: USER_DATA_QUERY_KEY });
  if (spaceId) {
    const spaceCreditsKey = TEAMSPACE_QUERY_KEYS.spaceCredits(spaceId);
    queryClient.setQueryData<SpaceCredits>(spaceCreditsKey, (spaceCredits) =>
      chargeSpaceRecordingMinutes(spaceCredits, recordingDurationS, isRecordingUnlimited),
    );
    void queryClient.invalidateQueries({ queryKey: spaceCreditsKey });
  }
};
